// ───────────────────────────────────────────────────────────────────────────
// Demo API resolver — answers every `makeRequest` call with fake data while the
// demo mode flag is on, so the real org-view UI runs end-to-end with zero
// backend contact. Unknown endpoints fall back to safe, non-crashing responses.
// ───────────────────────────────────────────────────────────────────────────

import { buildFakeJwt, DEMO_ORG_ID, DEMO_USER_ID } from './demoMode';
import {
  demoOrganization,
  updateDemoOrganization,
  getChatMessages,
  searchDemoChatMessages,
  getDemoMessageContext,
  getThreadReplies,
  getDemoPinnedMessages,
  pinDemoChatMessage,
  DEMO_SILENT_WAV,
  addDemoChatMessage,
  demoOrgMembers,
  getTasks,
  submitDemoTask,
  getDemoPendingContract,
  completeDemoSignContractTask,
  DEMO_CONTRACT_ID,
  createDemoTask,
  updateDemoTask,
  getInvoices,
  addDemoInvoice,
  deleteDemoInvoice,
  setDemoInvoiceEntered,
  getReports,
  getReportDetails,
  demoOrganizationUsers,
  demoSubscription,
  getDemoPaymentMonitoring,
  getDemoPaymentFollowUpTags,
  createDemoPaymentFollowUpTag,
  updateDemoPaymentFollowUpTag,
  deleteDemoPaymentFollowUpTag,
  setDemoOrganizationFollowUpTags,
  getDemoProfitCenters,
  updateDemoProfitCenterAssignment,
  getDemoStaffUsers,
  buildFinancialSummary,
  buildDemoWafeqProfitAndLoss,
  buildDemoWafeqBalanceSheet,
  buildDemoWafeqCashFlow,
  buildDemoPeriodOptions,
  buildDemoFinancialView,
  demoNotifications,
  demoAccountants,
  demoMeetingCredits,
  demoChatbotWelcome,
  getDemoMonthlyClosure,
  updateDemoMonthClosure,
  getDemoClientProjectProgress,
  getDemoUserProfile,
  updateDemoUserProfile,
  getDemoActiveContractTemplate,
  getDemoContractTemplateVersions,
  getDemoContractTemplateByVersion,
  saveDemoContractTemplate,
  activateDemoContractTemplateVersion,
  resetDemoContractTemplate,
  deleteDemoContractTemplateVersion,
  getDemoResponseTime,
} from './demoData';

export interface DemoRequest {
  url: string;
  method: string;
  data?: any;
  params?: any;
}

const DEMO_LATENCY_MS = 120;

const respond = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), DEMO_LATENCY_MS));

// Strip the base URL and any query string, normalise trailing slash.
const cleanPath = (rawUrl: string): string => {
  let path = rawUrl;
  const schemeIdx = path.indexOf('://');
  if (schemeIdx !== -1) {
    const afterScheme = path.slice(schemeIdx + 3);
    const slash = afterScheme.indexOf('/');
    path = slash === -1 ? '/' : afterScheme.slice(slash);
  }
  path = path.split('?')[0];
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  return path;
};

export const resolveDemoRequest = (req: DemoRequest): Promise<any> => {
  const method = (req.method || 'get').toLowerCase();
  const path = cleanPath(req.url || '');
  const params = req.params || {};
  const data = req.data || {};

  // ── Auth / token refresh ────────────────────────────────────────────────
  if (path.includes('refresh-token')) {
    const token = buildFakeJwt();
    return respond({ access_token: token, refresh_token: token });
  }

  // ── Chat ─────────────────────────────────────────────────────────────────
  if (path.startsWith('/chat')) {
    if (path === '/chat/response-time' && method === 'get') {
      return respond(getDemoResponseTime(params));
    }
    if (method === 'post' && path === '/chat') {
      const msg = addDemoChatMessage(String(data.message ?? ''));
      return respond({ chat_message: msg });
    }
    if (method === 'post' && path === '/chat/chatbot/navigate') {
      return respond({ is_tree_mode: false, options: [] });
    }
    if (method === 'post' && path === '/chat/update-last-read') {
      return respond({});
    }
    if (path.startsWith('/chat/chatbot/welcome/')) {
      return respond(demoChatbotWelcome);
    }
    if (/\/chat\/[^/]+\/members$/.test(path)) {
      return respond(demoOrgMembers);
    }
    if (/\/chat\/[^/]+\/thread\/[^/]+$/.test(path)) {
      const parentId = path.split('/').pop() || '';
      return respond(getThreadReplies(parentId));
    }
    if (/\/chat\/[^/]+\/read-details$/.test(path)) {
      return respond({ message_id: params.message_id || '', total_members: demoOrgMembers.length, read_by: [], not_read_by: [] });
    }
    if (/\/chat\/[^/]+\/message\/[^/]+\/playback-url$/.test(path)) {
      return respond({ playback_url: DEMO_SILENT_WAV, expires_in: 3600 });
    }
    if (/\/chat\/[^/]+\/asset\/[^/]+\/url$/.test(path)) {
      return respond({ url: DEMO_SILENT_WAV, expires_in: 3600 });
    }
    if (method === 'patch' && /\/chat\/[^/]+\/message\/[^/]+\/pin$/.test(path)) {
      const parts = path.split('/');
      const messageId = parts[parts.length - 2] || '';
      try {
        return respond(pinDemoChatMessage(messageId, Boolean(data?.is_pinned)));
      } catch (error) {
        return Promise.reject(error);
      }
    }
    if (method === 'get' && /\/chat\/[^/]+\/pinned$/.test(path)) {
      return respond(getDemoPinnedMessages());
    }
    if (method === 'patch' && /\/chat\/[^/]+\/message\/[^/]+$/.test(path)) {
      return respond({ message_id: path.split('/').pop(), message: String(data.message ?? ''), is_edited: true, edited_at: new Date().toISOString() });
    }
    if (method === 'get' && /\/chat\/[^/]+\/search$/.test(path)) {
      const limit = Number(params.limit) || 50;
      const results = searchDemoChatMessages(String(params.q ?? ''), limit);
      return respond({ results, total_count: results.length });
    }
    if (method === 'get' && /\/chat\/[^/]+\/context\/[^/]+$/.test(path)) {
      const messageId = path.split('/').pop() || '';
      const before = Number(params.before) || 20;
      const after = Number(params.after) || 20;
      return respond(getDemoMessageContext(messageId, before, after));
    }
    // GET messages: /chat/:orgId
    if (/^\/chat\/[^/]+$/.test(path)) {
      const messages = getChatMessages();
      return respond({ result_list: messages, total_count: messages.length, organization_members_count: demoOrgMembers.length });
    }
  }

  // ── Reporting tasks ────────────────────────────────────────────────────────
  if (path.startsWith('/reporting-tasks')) {
    if (method === 'post' && path === '/reporting-tasks') {
      return respond(createDemoTask(data));
    }
    if (method === 'post' && /\/reporting-tasks\/[^/]+\/submit$/.test(path)) {
      const taskId = path.split('/')[2];
      return respond(submitDemoTask(taskId) || {});
    }
    if (method === 'patch' && /^\/reporting-tasks\/[^/]+$/.test(path)) {
      const taskId = path.split('/')[2];
      return respond(updateDemoTask(taskId, data) || {});
    }
    if (method === 'delete' && /^\/reporting-tasks\/[^/]+$/.test(path)) {
      return respond({ message: 'Task deleted successfully' });
    }
    if (method === 'get' && /\/reporting-tasks\/[^/]+\/download$/.test(path)) {
      return respond({ download_url: '#demo', expires_at: new Date(Date.now() + 3600_000).toISOString(), filename: 'demo-attachment.pdf' });
    }
    if (/\/reporting-tasks\/organization\/[^/]+$/.test(path) || path === '/reporting-tasks/my-tasks') {
      const tasks = getTasks();
      return respond({ tasks, total: tasks.length });
    }
    if (/^\/reporting-tasks\/[^/]+$/.test(path)) {
      const taskId = path.split('/')[2];
      return respond(getTasks().find((t) => t.id === taskId) || getTasks()[0]);
    }
  }

  // ── Documents (invoices) ────────────────────────────────────────────────────
  if (path.startsWith('/invoice')) {
    if (method === 'post' && path === '/invoice') {
      return respond(addDemoInvoice(data));
    }
    if (method === 'post' && path === '/invoice/bulk') {
      const arr = Array.isArray(data) ? data : [];
      return respond(arr.map((d: any) => addDemoInvoice(d)));
    }
    if (method === 'patch' && /^\/invoice\/[^/]+\/entered$/.test(path)) {
      try {
        return respond(setDemoInvoiceEntered(path.split('/')[2], Boolean(data?.entered)));
      } catch (error) {
        return Promise.reject(error);
      }
    }
    if (method === 'delete' && /^\/invoice\/[^/]+$/.test(path)) {
      deleteDemoInvoice(path.split('/')[2]);
      return respond({ success: true });
    }
    if (method === 'put' && /^\/invoice\/[^/]+$/.test(path)) {
      const id = path.split('/')[2];
      return respond(getInvoices().find((i) => i.id === id) || { success: true });
    }
    if (method === 'post' && /process-ocr/.test(path)) {
      return respond({ success: true });
    }
    if (method === 'get' && /^\/invoice\/[^/]+$/.test(path)) {
      const id = path.split('/')[2];
      return respond(getInvoices().find((i) => i.id === id) || getInvoices()[0]);
    }
    if (method === 'get' && path === '/invoice') {
      const all = getInvoices();
      let list = all;
      if (params.type === 'folder') {
        list = all.filter((i) => i.type === 'folder');
      } else if (params.search) {
        const q = String(params.search).toLowerCase();
        list = all.filter((i) => i.filename.toLowerCase().includes(q));
      } else {
        const parent = params.parent_id || undefined;
        list = all.filter((i) => (i.parent_id || undefined) === parent);
      }
      if (params.type !== 'folder' && (params.entered === true || params.entered === 'true')) {
        list = list.filter((i) => i.entered === true);
      } else if (params.type !== 'folder' && (params.entered === false || params.entered === 'false')) {
        list = list.filter((i) => i.entered !== true);
      }
      return respond({ result_list: list, total_count: list.length });
    }
  }

  // ── Reports ──────────────────────────────────────────────────────────────────
  if (path.startsWith('/report')) {
    if (method === 'get' && path === '/report/period-options') {
      return respond(buildDemoPeriodOptions(params.organization_id || DEMO_ORG_ID));
    }
    if (method === 'get' && path === '/report/financial-view') {
      return respond(buildDemoFinancialView(params));
    }
    if (method === 'get' && /^\/report\/[^/]+$/.test(path)) {
      return respond(getReportDetails(path.split('/')[2]));
    }
    if (method === 'post' && /\/(approve|reject|client-decision)$/.test(path)) {
      return respond(getReportDetails(path.split('/')[2]));
    }
    if (method === 'get' && path === '/report') {
      const list = getReports();
      return respond({ result_list: list, total_count: list.length });
    }
    if (method === 'delete' && /^\/report\/[^/]+$/.test(path)) {
      return respond({ success: true });
    }
  }

  // ── User profile ────────────────────────────────────────────────────────────
  if (path === '/user/me' && method === 'get') {
    return respond(getDemoUserProfile());
  }
  if (path === '/user' && method === 'get') {
    const list = getDemoStaffUsers();
    return respond({ result_list: list, total_count: list.length });
  }
  if (/^\/user\/[^/]+$/.test(path) && method === 'patch') {
    return respond(updateDemoUserProfile(data));
  }

  // ── Organization users (settings) ─────────────────────────────────────────────
  if (/\/user\/organization\/[^/]+\/users$/.test(path) && method === 'get') {
    return respond({ result_list: demoOrganizationUsers, total_count: demoOrganizationUsers.length });
  }

  // ── Profit Center ───────────────────────────────────────────────────────────
  if (path === '/profit-center' && method === 'get') {
    return respond(getDemoProfitCenters(
      typeof params.month === 'string' ? params.month : undefined,
      params.location === 'EGY' || params.location === 'SYR' ? params.location : undefined,
    ));
  }
  if (/^\/profit-center\/assignments\/[^/]+$/.test(path) && method === 'patch') {
    try {
      return respond(updateDemoProfitCenterAssignment(path.split('/').pop()!, data ?? {}));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // ── Subscription ──────────────────────────────────────────────────────────────
  if (path.startsWith('/subscription')) {
    if (path === '/subscription/admin/payment-monitoring' && method === 'get') {
      return respond(getDemoPaymentMonitoring(params));
    }
    if (path === '/subscription/admin/payment-monitoring/sync-invoices' && method === 'post') {
      return respond({ synced_count: 0, updated_count: 0, failed_count: 0 });
    }
    if (path === '/subscription/admin/payment-follow-up-tags' && method === 'get') {
      return respond(getDemoPaymentFollowUpTags());
    }
    if (path === '/subscription/admin/payment-follow-up-tags' && method === 'post') {
      try {
        return respond(createDemoPaymentFollowUpTag(data?.name, data?.sort_order));
      } catch (error) {
        return Promise.reject(error);
      }
    }
    if (/^\/subscription\/admin\/payment-follow-up-tags\/[^/]+$/.test(path) && method === 'put') {
      const tagId = path.split('/').pop()!;
      try {
        return respond(updateDemoPaymentFollowUpTag(tagId, data ?? {}));
      } catch (error) {
        return Promise.reject(error);
      }
    }
    if (/^\/subscription\/admin\/payment-follow-up-tags\/[^/]+$/.test(path) && method === 'delete') {
      try {
        deleteDemoPaymentFollowUpTag(path.split('/').pop()!);
        return respond({});
      } catch (error) {
        return Promise.reject(error);
      }
    }
    if (
      /^\/subscription\/admin\/organizations\/[^/]+\/payment-follow-up-tags$/.test(path)
      && method === 'put'
    ) {
      const organizationId = path.split('/')[4];
      return respond(setDemoOrganizationFollowUpTags(organizationId, data?.tag_ids ?? []));
    }
    if (/\/subscription\/organization\/[^/]+$/.test(path)) {
      return respond({ result: demoSubscription });
    }
    if (path === '/subscription/plans' || path === '/subscription/admin/plans') {
      return respond({ result: [demoSubscription.plan] });
    }
  }

  // ── Organization + add-ons ──────────────────────────────────────────────────────
  if (path.startsWith('/organization')) {
    if (/\/addons\/wafeq\/status$/.test(path)) {
      return respond({
        connected: true,
        expires_at: demoSubscription.expires_at,
        addon_id: 'demo-wafeq',
        reports_source: 'wafeq',
        wafeq_report_types: ['profit_and_loss', 'balance_sheet', 'cash_flow'],
        mohasby_report_types: ['other', 'tax_return', 'zakat_return'],
      });
    }
    if (/\/addons\/daftra\/status$/.test(path)) {
      return respond({
        connected: false,
        subdomain: null,
        addon_id: null,
        connected_at: null,
      });
    }
    if (/\/addons\/daftra\/financial-summary$/.test(path)) {
      return respond(buildFinancialSummary(params.date_after || '2026-01-01', params.date_before || '2026-06-30'));
    }
    if (/\/addons\/daftra\/connect$/.test(path) && method === 'post') {
      return respond({
        connected: true,
        subdomain: data?.subdomain || 'demo',
        addon_id: 'demo-daftra',
        connected_at: new Date().toISOString(),
      });
    }
    if (/\/addons\/daftra\/disconnect$/.test(path)) {
      return respond({ detail: 'Daftra disconnected successfully' });
    }
    if (/\/addons\/qoyod\/status$/.test(path)) {
      return respond({
        connected: false,
        addon_id: null,
        connected_at: null,
      });
    }
    if (/\/addons\/qoyod\/financial-summary$/.test(path)) {
      return respond(buildFinancialSummary(params.date_after || '2026-01-01', params.date_before || '2026-06-30'));
    }
    if (/\/addons\/qoyod\/connect$/.test(path) && method === 'post') {
      return respond({
        connected: true,
        addon_id: 'demo-qoyod',
        connected_at: new Date().toISOString(),
      });
    }
    if (/\/addons\/qoyod\/disconnect$/.test(path)) {
      return respond({ detail: 'Qoyod disconnected successfully' });
    }
    if (/\/addons\/rekaz\/status$/.test(path)) {
      return respond({
        connected: false,
        tenant_id: null,
        addon_id: null,
        connected_at: null,
      });
    }
    if (/\/addons\/rekaz\/financial-summary$/.test(path)) {
      return respond(buildFinancialSummary(params.date_after || '2026-01-01', params.date_before || '2026-06-30'));
    }
    if (/\/addons\/rekaz\/connect$/.test(path) && method === 'post') {
      return respond({
        connected: true,
        tenant_id: data?.tenant_id || 'demo-tenant',
        addon_id: 'demo-rekaz',
        connected_at: new Date().toISOString(),
      });
    }
    if (/\/addons\/rekaz\/disconnect$/.test(path)) {
      return respond({ detail: 'Rekaz disconnected successfully' });
    }
    if (/\/addons\/odoo\/status$/.test(path)) {
      return respond({
        connected: false,
        addon_id: null,
        connected_at: null,
        url: null,
        login: null,
        database: null,
        company_name: null,
      });
    }
    if (/\/addons\/odoo\/financial-summary$/.test(path)) {
      return respond(buildFinancialSummary(params.date_after || '2026-01-01', params.date_before || '2026-06-30'));
    }
    if (/\/addons\/odoo\/connect$/.test(path) && method === 'post') {
      return respond({
        connected: true,
        addon_id: 'demo-odoo',
        connected_at: new Date().toISOString(),
        url: data?.url || 'https://company.odoo.com',
        login: data?.login || 'user@company.com',
        database: data?.database || 'company',
        company_name: 'Demo Company',
      });
    }
    if (/\/addons\/odoo\/disconnect$/.test(path)) {
      return respond({ detail: 'Odoo disconnected successfully' });
    }
    if (/\/addons\/zoho\/status$/.test(path)) {
      return respond({
        connected: false,
        addon_id: null,
        connected_at: null,
        expires_at: null,
        zoho_organization_id: null,
        zoho_organization_name: null,
      });
    }
    if (/\/addons\/zoho\/financial-summary$/.test(path)) {
      return respond(buildFinancialSummary(params.date_after || '2026-01-01', params.date_before || '2026-06-30'));
    }
    if (/\/addons\/zoho\/connect$/.test(path)) {
      return respond({ authorization_url: '#demo' });
    }
    if (/\/addons\/zoho\/disconnect$/.test(path)) {
      return respond({ detail: 'Zoho Books disconnected successfully' });
    }
    if (/\/addons\/wafeq\/reports\/profit-and-loss$/.test(path)) {
      return respond(buildDemoWafeqProfitAndLoss(
        params.date_after || '2026-01-01',
        params.date_before || '2026-06-30',
      ));
    }
    if (/\/addons\/wafeq\/reports\/balance-sheet$/.test(path)) {
      return respond(buildDemoWafeqBalanceSheet(params.date || '2026-06-30'));
    }
    if (/\/addons\/wafeq\/reports\/cash-flow$/.test(path)) {
      return respond(buildDemoWafeqCashFlow(
        params.date_after || '2026-01-01',
        params.date_before || '2026-06-30',
      ));
    }
    if (/\/addons\/wafeq\/financial-summary$/.test(path)) {
      return respond(buildFinancialSummary(params.date_after || '2026-01-01', params.date_before || '2026-06-30'));
    }
    if (/\/addons\/wafeq\/connect$/.test(path)) {
      return respond({ authorization_url: '#demo' });
    }
    if (/\/addons\/interest$/.test(path)) {
      return respond({ already_registered: false });
    }
    if (/\/meetings\/credits$/.test(path)) {
      return respond({ ...demoMeetingCredits });
    }
    if (/\/meetings\/weekly-bonus$/.test(path) && method === 'patch') {
      const bonus = Math.min(10, Math.max(0, Number(data?.weekly_bonus ?? 0)));
      demoMeetingCredits.weekly_bonus = bonus;
      demoMeetingCredits.weekly_limit = demoMeetingCredits.weekly_base_limit + bonus;
      demoMeetingCredits.weekly_remaining = Math.max(
        0,
        demoMeetingCredits.weekly_limit - demoMeetingCredits.weekly_used,
      );
      return respond({
        weekly_bonus: demoMeetingCredits.weekly_bonus,
        weekly_limit: demoMeetingCredits.weekly_limit,
      });
    }
    if (/\/accountants$/.test(path)) {
      return respond(demoAccountants);
    }
    if (/\/addon\/organization\/[^/]+\/addons$/.test(path)) {
      return respond([]);
    }
    if (path === '/organization' && method === 'get') {
      const startedMonth = typeof params.subscription_started_month === 'string' ? params.subscription_started_month : '';
      const expiresMonth = typeof params.subscription_expires_month === 'string' ? params.subscription_expires_month : '';
      const startedAt = demoOrganization.subscription?.subscription_started_at ?? '';
      const expiresAt = demoOrganization.subscription?.subscription_expires_at ?? '';
      const software = typeof params.accounting_software === 'string' ? params.accounting_software.trim().toLowerCase() : '';
      const currentSoftware = String(demoOrganization.accounting_software ?? '').trim().toLowerCase();
      const matchesSoftware = !software || currentSoftware === software;
      const matchesMonth = (!startedMonth || startedAt.startsWith(startedMonth))
        && (!expiresMonth || expiresAt.startsWith(expiresMonth))
        && matchesSoftware;
      if (!matchesMonth) {
        return respond({ result_list: [], total_count: 0 });
      }
      const withAssignees = params.with_assignees === true || params.with_assignees === 'true';
      const org = withAssignees
        ? {
            ...demoOrganization,
            accountants: [{ id: 'demo-accountant-0001', name: 'المحاسب أحمد', email: 'ahmed@mohasby.com' }],
            clearing_officers: [],
          }
        : demoOrganization;
      return respond({ result_list: [org], total_count: 1 });
    }
    if (path === '/organization' && method === 'post') {
      return respond(updateDemoOrganization(data));
    }
    if (method === 'put' && /^\/organization\/[^/]+$/.test(path)) {
      return respond(updateDemoOrganization(data));
    }
    if (/^\/organization\/[^/]+\/accept-terms$/.test(path) && method === 'post') {
      return respond(updateDemoOrganization({
        terms_accepted_at: new Date().toISOString(),
        terms_accepted_by_user_id: DEMO_USER_ID,
      }));
    }
    if (/^\/organization\/[^/]+$/.test(path)) {
      return respond(demoOrganization);
    }
    // reporting-type patch and other org writes
    return respond(demoOrganization);
  }

  // ── Add-on credentials endpoints ────────────────────────────────────────────────
  if (path.startsWith('/addon')) {
    return respond(method === 'get' ? [] : { success: true });
  }

  // ── Notifications ────────────────────────────────────────────────────────────────
  if (path.startsWith('/notification')) {
    if (method === 'get' && path === '/notification/my-organizations') {
      const unreadOnly = params.unread_only !== false && params.unread_only !== 'false';
      const list = demoNotifications.result_list.filter((notification) => !unreadOnly || !notification.is_read);
      return respond({ result_list: list, total_count: list.length });
    }
    if (method === 'get') return respond(demoNotifications);
    return respond({});
  }

  // ── Uploads ──────────────────────────────────────────────────────────────────────
  if (path === '/upload' && method === 'post') {
    return respond({ id: `demo-asset-${Date.now()}`, upload_url: '#demo-upload' });
  }

  // ── Signed download URLs (reports / invoices / folder zips) ─────────────────────────
  if (/\/(download|download-zip)$/.test(path)) {
    return respond({ download_url: '#demo', expires_at: new Date(Date.now() + 3600_000).toISOString(), filename: 'demo-file' });
  }

  // ── Internal chat staff meetings (no credits) ──────────────────────────────────
  if (/^\/internal-chat\/[^/]+\/meetings\/availability$/.test(path)) {
    return respond({
      time_min: params?.start,
      time_max: params?.end,
      busy: [],
    });
  }
  if (/^\/internal-chat\/[^/]+\/meetings$/.test(path) && method === 'post') {
    const attendeeIds = Array.from(new Set([
      data?.accountant_id,
      DEMO_USER_ID,
      ...((data?.attendee_user_ids as string[] | undefined) ?? []),
    ].filter(Boolean)));
    return respond({
      meeting_id: `01DEMOICM${Date.now()}`,
      google_event_id: 'demo-internal',
      meet_link: 'https://meet.google.com/demo-xxxx-yyy',
      start: data.start,
      end: data.end,
      accountant_id: data.accountant_id,
      attendee_user_ids: attendeeIds,
    });
  }
  if (/^\/internal-chat\/[^/]+\/members$/.test(path)) {
    return respond(demoOrgMembers.map((member, index) => ({
      ...member,
      role: index === 0 ? 'accountant' : 'admin',
    })));
  }

  // ── Calendar / meetings ─────────────────────────────────────────────────────────
  if (/\/google\/status$/.test(path)) {
    return respond({ connected: true, email: 'ahmed@mohasby.com' });
  }
  if (/\/availability$/.test(path)) {
    return respond({ busy: [] });
  }
  if (/\/meetings$/.test(path) && method === 'post') {
    demoMeetingCredits.monthly_used = Math.min(demoMeetingCredits.monthly_limit, demoMeetingCredits.monthly_used + 1);
    demoMeetingCredits.monthly_remaining = Math.max(0, demoMeetingCredits.monthly_limit - demoMeetingCredits.monthly_used);
    demoMeetingCredits.weekly_used = Math.min(demoMeetingCredits.weekly_limit, demoMeetingCredits.weekly_used + 1);
    demoMeetingCredits.weekly_remaining = Math.max(0, demoMeetingCredits.weekly_limit - demoMeetingCredits.weekly_used);
    return respond({
      meet_link: 'https://meet.google.com/demo-xxxx-yyy',
      meeting_id: '01DEMO0000000000000000000',
      google_event_id: 'demo',
      start: data.start,
      end: data.end,
      meeting_type: data.meeting_type ?? 'first_meeting',
      description: data.description ?? null,
      monthly_limit: demoMeetingCredits.monthly_limit,
      monthly_used: demoMeetingCredits.monthly_used,
      monthly_remaining: demoMeetingCredits.monthly_remaining,
      weekly_base_limit: demoMeetingCredits.weekly_base_limit,
      weekly_bonus: demoMeetingCredits.weekly_bonus,
      weekly_limit: demoMeetingCredits.weekly_limit,
      weekly_used: demoMeetingCredits.weekly_used,
      weekly_remaining: demoMeetingCredits.weekly_remaining,
      show_rating_popup: false,
    });
  }

  // ── Client project progress ─────────────────────────────────────────────────
  const clientProgressMatch = path.match(/^\/accountant-projects\/organization\/([^/]+)\/client-progress$/);
  if (clientProgressMatch && method === 'get') {
    return respond(getDemoClientProjectProgress(decodeURIComponent(clientProgressMatch[1])));
  }

  // ── Monthly closure ─────────────────────────────────────────────────────────
  if (path.startsWith('/monthly-closure/organization/')) {
    const segments = path.split('/');
    const orgId = segments[3];
    if (method === 'get') {
      const year = Number(params.year ?? new Date().getFullYear());
      return respond(getDemoMonthlyClosure(orgId, year));
    }
    if (method === 'patch' && segments.length >= 6) {
      const year = Number(segments[4]);
      const month = Number(segments[5]);
      try {
        return respond(updateDemoMonthClosure(orgId, year, month, data.status));
      } catch (err) {
        return Promise.reject(err);
      }
    }
  }

  // ── Contracts ───────────────────────────────────────────────────────────────────
  if (path.startsWith('/contracts')) {
    if (path === '/contracts/admin/template/active') {
      if (method === 'get') return respond({ result: getDemoActiveContractTemplate() });
      if (method === 'put') {
        return respond({ result: saveDemoContractTemplate({ html_body: data.html_body, name: data.name, notes: data.notes }) });
      }
    }
    if (path === '/contracts/admin/template/versions' && method === 'get') {
      return respond({ result: getDemoContractTemplateVersions() });
    }
    const adminVersionMatch = path.match(/^\/contracts\/admin\/template\/versions\/([^/]+)$/);
    if (adminVersionMatch && method === 'get') {
      const tpl = getDemoContractTemplateByVersion(decodeURIComponent(adminVersionMatch[1]));
      if (!tpl) return Promise.reject({ response: { status: 404 } });
      return respond({ result: tpl });
    }
    if (adminVersionMatch && method === 'delete') {
      const result = deleteDemoContractTemplateVersion(decodeURIComponent(adminVersionMatch[1]));
      if (!result.ok) return Promise.reject({ response: { status: result.status } });
      return respond({ result: null });
    }
    const adminActivateMatch = path.match(/^\/contracts\/admin\/template\/versions\/([^/]+)\/activate$/);
    if (adminActivateMatch && method === 'post') {
      const tpl = activateDemoContractTemplateVersion(decodeURIComponent(adminActivateMatch[1]));
      if (!tpl) return Promise.reject({ response: { status: 404 } });
      return respond({ result: tpl });
    }
    if (path === '/contracts/admin/template/preview' && method === 'post') {
      const html = data.html_body || getDemoActiveContractTemplate().html_body;
      return respond({
        result: {
          html: html.replace(/\{\{\s*organization_name\s*\}\}/g, demoOrganization.name),
          pdf_base64: data.include_pdf === false ? null : btoa('%PDF-1.4 demo'),
        },
      });
    }
    if (path === '/contracts/admin/template/reset' && method === 'post') {
      return respond({ result: resetDemoContractTemplate() });
    }

    if (path === '/contracts/pending') {
      return respond({ result: getDemoPendingContract() });
    }
    if (path === '/contracts/admin/create' && method === 'post') {
      const orgId = (data as { organization_id?: string })?.organization_id ?? demoOrganization.id;
      const contractId = 'demo-contract-new';
      return respond({
        data: {
          contract: {
            id: contractId,
            organization_id: orgId,
            status: 'pending_signature',
            organization_name: demoOrganization.name,
            plan_code: demoSubscription.plan.code,
            plan_name_ar: demoSubscription.plan.name_ar,
            plan_name_en: demoSubscription.plan.name_en,
            billing_cycle: demoSubscription.billing_cycle,
            effective_date: demoSubscription.started_at,
            period_start: demoSubscription.started_at,
            period_end: demoSubscription.expires_at,
            total_fee: demoSubscription.plan.prices.annual ?? '12000',
            installments_total: null,
            services: [{ ar: 'محاسب عن بُعد', en: 'Remote accountant' }],
            sign_url_path: `/contracts/${contractId}/sign`,
            signer_name: null,
            national_id: null,
            cr_number: null,
            registered_address: null,
            signed_at: null,
            document_hash: null,
            pdf_asset_id: null,
            invoice_id: null,
          },
          email_sent: Boolean(demoOrganization.email),
          message_ar: 'تم إنشاء العقد بنجاح. يرجى على العميل مراجعته وتوقيعه.',
          message_en: 'Contract created successfully. The client should review and sign it.',
        },
      });
    }
    if (path === '/contracts/wathq-config' && method === 'get') {
      return respond({ result: { enabled: false } });
    }
    const verifyMatch = path.match(/^\/contracts\/([^/]+)\/verify$/);
    if (verifyMatch && method === 'post') {
      return respond({
        result: {
          verified: true,
          wathq_required: false,
          can_sign: true,
          manual_mode: true,
        },
      });
    }
    const contractMatch = path.match(/^\/contracts\/([^/]+)$/);
    if (contractMatch && method === 'get') {
      return respond({
        result: {
          id: contractMatch[1],
          organization_id: demoOrganization.id,
          status: 'pending_signature',
          organization_name: demoOrganization.name,
          plan_code: demoSubscription.plan.code,
          plan_name_ar: demoSubscription.plan.name_ar,
          plan_name_en: demoSubscription.plan.name_en,
          billing_cycle: demoSubscription.billing_cycle,
          effective_date: demoSubscription.started_at,
          period_start: demoSubscription.started_at,
          period_end: demoSubscription.expires_at,
          total_fee: demoSubscription.plan.prices.annual ?? '12000',
          installments_total: null,
          services: [{ ar: 'محاسب عن بُعد', en: 'Remote accountant' }],
          sign_url_path: `/contracts/${contractMatch[1]}/sign`,
          signer_name: null,
          national_id: null,
          cr_number: null,
          registered_address: null,
          signed_at: null,
          document_hash: null,
          pdf_asset_id: null,
          invoice_id: null,
        },
      });
    }
    const previewMatch = path.match(/^\/contracts\/([^/]+)\/preview$/);
    if (previewMatch && method === 'get') {
      return respond({
        result: {
          html: '<div style="font-family:sans-serif;padding:16px"><h2>معاينة العقد</h2><p>هذه معاينة تجريبية للعقد.</p></div>',
        },
      });
    }
    const signMatch = path.match(/^\/contracts\/([^/]+)\/sign$/);
    if (signMatch && method === 'post') {
      completeDemoSignContractTask();
      return respond({
        result: {
          contract: {
            id: signMatch[1],
            organization_id: demoOrganization.id,
            status: 'signed',
            organization_name: demoOrganization.name,
            plan_code: demoSubscription.plan.code,
            plan_name_ar: demoSubscription.plan.name_ar,
            plan_name_en: demoSubscription.plan.name_en,
            billing_cycle: demoSubscription.billing_cycle,
            effective_date: demoSubscription.started_at,
            period_start: demoSubscription.started_at,
            period_end: demoSubscription.expires_at,
            total_fee: demoSubscription.plan.prices.annual ?? '12000',
            installments_total: null,
            services: [{ ar: 'محاسب عن بُعد', en: 'Remote accountant' }],
            sign_url_path: `/contracts/${signMatch[1]}/sign`,
            signer_name: data?.signer_name ?? 'Demo User',
            national_id: data?.national_id ?? '1234567890',
            cr_number: data?.cr_number ?? '1234567890',
            registered_address: data?.registered_address ?? 'Riyadh',
            signed_at: new Date().toISOString(),
            document_hash: 'demo-hash',
            pdf_asset_id: 'demo-pdf',
            invoice_id: 'demo-invoice',
          },
          message_ar: 'تم توقيع العقد بنجاح',
          message_en: 'Contract signed successfully',
        },
      });
    }
    const downloadMatch = path.match(/^\/contracts\/([^/]+)\/download$/);
    if (downloadMatch && method === 'get') {
      return respond({
        download_url: '#demo-contract-download',
        expires_at: new Date(Date.now() + 3600000).toISOString(),
        filename: 'contract.pdf',
      });
    }
  }

  // ── Generic safe fallbacks ─────────────────────────────────────────────────────────
  if (method === 'get') {
    return respond({ result_list: [], total_count: 0, tasks: [], total: 0, periods: [], accountants: [], result: null, data: {} });
  }
  return respond({ success: true });
};
